import React, { useState, useRef } from 'react';
import api from '../config/axios';
import datasetOptions from '../dataset.json';

const UploadForm = ({ onUploadSuccess }) => {
  const [dataset, setDataset] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success', 'error', 'warning'
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    console.log('File selection triggered:', e.target.files);
    const selectedFiles = Array.from(e.target.files);
    console.log('Selected files:', selectedFiles);
    setFiles(selectedFiles);
    setMessage('');
    setMessageType('');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    console.log('Files dropped:', e.dataTransfer.files);
    const droppedFiles = Array.from(e.dataTransfer.files);
    console.log('Dropped files array:', droppedFiles);
    setFiles(droppedFiles);
    setMessage('');
    setMessageType('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!dataset.trim()) {
      setMessage('Please select a dataset name');
      setMessageType('error');
      return;
    }
    
    if (files.length === 0) {
      setMessage('Please select files to upload');
      setMessageType('error');
      return;
    }

    // Log file details for debugging
    console.log('Uploading files:', files.map(f => ({
      name: f.name,
      size: f.size,
      sizeMB: (f.size / 1024 / 1024).toFixed(2),
      type: f.type
    })));

    // Validate file types
    const validExtensions = ['.json', '.bin', '.model', '.pkl', '.h5', '.onnx', '.pt', '.pth'];
    const invalidFiles = files.filter(file => {
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      return !validExtensions.includes(fileExtension);
    });
    
    if (invalidFiles.length > 0) {
      setMessage(`Invalid file types detected. Only ${validExtensions.join(', ')} files are allowed. Invalid files: ${invalidFiles.map(f => f.name).join(', ')}`);
      setMessageType('error');
      return;
    }

    // Check for very large files and warn user
    const largeFiles = files.filter(file => file.size > 100 * 1024 * 1024); // 100MB
    if (largeFiles.length > 0) {
      console.warn('Large files detected:', largeFiles.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(2)}MB)`));
      setMessage(`⚠️ Large files detected. Upload may take several minutes. Files: ${largeFiles.map(f => f.name).join(', ')}`);
      setMessageType('warning');
      // Continue with upload but show warning
    }

    // Check total file size to warn about potential 413 errors
    const totalSizeMB = files.reduce((total, file) => total + (file.size / 1024 / 1024), 0);
    if (totalSizeMB > 5) { // Warn if total size > 5MB
      console.warn('Total upload size:', totalSizeMB.toFixed(2) + 'MB');
      if (totalSizeMB > 10) { // High risk of 413 error
        setMessage(`⚠️ Large total upload size (${totalSizeMB.toFixed(2)}MB). If you get a "413 Request Entity Too Large" error, this is a server configuration issue.`);
        setMessageType('warning');
      }
    }

    setLoading(true);
    setMessage('');
    setMessageType('');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('dataset', dataset);
      
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await api.post('/admin/models/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 600000, // 10 minutes for upload
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percentCompleted}%`);
            setUploadProgress(percentCompleted);
          }
        },
      });

      // Check if response has uploadedFiles or success message
      if (response.data.uploadedFiles || response.data.message?.includes('successfully')) {
        const uploadedFiles = response.data.uploadedFiles || [];
        const dataset = response.data.dataset || dataset;
        const totalFiles = response.data.totalFiles || uploadedFiles.length;
        
        setMessage(`✅ Upload successful! ${totalFiles} file(s) uploaded to dataset "${dataset}". Files: ${uploadedFiles.map(f => f.originalName || f.filename).join(', ')}`);
        setMessageType('success');
        setDataset('');
        setFiles([]); // Clear the selected files
        if (fileInputRef.current) {
          fileInputRef.current.value = ''; // Reset the file input
        }
        onUploadSuccess();
      } else {
        setMessage('❌ Upload failed: Server returned an error');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        setMessage('❌ Upload timed out. This usually happens with large files. Please try again or contact support if the issue persists.');
        setMessageType('error');
      } else if (error.response?.status === 401) {
        setMessage('❌ Authentication failed. Please log in again.');
        setMessageType('error');
      } else if (error.response?.status === 413) {
        // Enhanced 413 error handling
        const fileSizeMB = files.reduce((total, file) => total + (file.size / 1024 / 1024), 0).toFixed(2);
        setMessage(`❌ File too large (${fileSizeMB}MB total). Server rejected the upload. This is a server-side configuration issue. Contact your administrator to increase the server's file size limit.`);
        setMessageType('error');
        
        // Log detailed information for debugging
        console.error('413 Error Details:', {
          totalFileSize: fileSizeMB + 'MB',
          individualFiles: files.map(f => ({ name: f.name, size: f.size, sizeMB: (f.size / 1024 / 1024).toFixed(2) })),
          serverResponse: error.response?.data,
          headers: error.response?.headers
        });
      } else if (error.response?.status === 400) {
        setMessage(`❌ Bad request: ${error.response.data?.error || 'Invalid data provided'}`);
        setMessageType('error');
      } else if (error.response?.status === 500) {
        setMessage('❌ Server error. Please try again later or contact support.');
        setMessageType('error');
      } else if (error.code === 'ERR_NETWORK') {
        // Enhanced network error handling
        if (error.message.includes('timeout')) {
          setMessage('❌ Network timeout. Large files may take longer to upload. Please try again.');
        } else if (error.message.includes('Network Error')) {
          setMessage('❌ Network connection lost. Please check your internet connection and try again.');
        } else {
          setMessage('❌ Network error. Please check your connection and try again.');
        }
        setMessageType('error');
      } else if (error.response?.data?.error) {
        setMessage(`❌ Upload failed: ${error.response.data.error}`);
        setMessageType('error');
      } else {
        setMessage('❌ Upload failed. Please try again.');
        setMessageType('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const getMessageClass = () => {
    switch (messageType) {
      case 'success':
        return 'alert alert-success';
      case 'error':
        return 'alert alert-error';
      case 'warning':
        return 'alert alert-warning';
      default:
        return 'alert';
    }
  };

  return (
    <div className="card">
      <h2>Upload Model Files</h2>
      
      {message && (
        <div className={getMessageClass()}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="dataset">Dataset Name:</label>
          <select
            id="dataset"
            value={dataset}
            onChange={(e) => setDataset(e.target.value)}
            required
          >
            <option value="">Select a dataset...</option>
            {datasetOptions.dataset.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Select Files:</label>
          <div 
            className={`upload-form ${isDragOver ? 'dragover' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              accept=".json,.JSON,.bin,.BIN,.model,.MODEL,.pkl,.PKL,.h5,.H5,.onnx,.ONNX,.pt,.PT,.pth,.PTH"
              style={{ 
                position: 'absolute',
                left: '-9999px',
                opacity: 0,
                pointerEvents: 'none'
              }}
              id="file-input"
            />
            <label htmlFor="file-input" style={{ cursor: 'pointer', color: '#007bff' }}>
              <strong>Click to select files</strong> or drag and drop here
            </label>
            <p style={{ margin: '10px 0 0 0', fontSize: '14px', color: '#666' }}>
              Supported formats: .json, .bin, .model, .pkl, .h5, .onnx, .pt, .pth (case-insensitive)
            </p>
            <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#888' }}>
              ⚠️ Large .bin files (&gt;100MB) may take several minutes to upload
            </p>
            <div style={{ marginTop: '15px' }}>
              <button 
                type="button" 
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                📁 Browse Files
              </button>
            </div>
          </div>
        </div>

        {files.length > 0 && (
          <div className="form-group">
            <label>Selected Files ({files.length}):</label>
            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
              {files.map((file, index) => (
                <li key={index}>
                  {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </li>
              ))}
            </ul>
          </div>
        )}

        <button 
          type="submit" 
          className="btn btn-primary"
          disabled={loading}
        >
          {loading ? '⏳ Uploading...' : '📤 Upload Files'}
        </button>

        {loading && uploadProgress > 0 && (
          <div className="form-group" style={{ marginTop: '15px' }}>
            <label>Upload Progress:</label>
            <div style={{ 
              width: '100%', 
              backgroundColor: '#f0f0f0', 
              borderRadius: '4px', 
              overflow: 'hidden',
              marginTop: '5px'
            }}>
              <div style={{
                width: `${uploadProgress}%`,
                height: '20px',
                backgroundColor: '#007bff',
                transition: 'width 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {uploadProgress}%
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default UploadForm; 